// How the sync events are shaped before they are reported: what a conflict scan counted,
// which type a document id belongs to, the one-line summary of what a pull brought down
// from another device, and the tally of the pulls the live path stays silent about.
//
// These live outside DatabaseWrapper because that class imports pouchdb-browser, which
// references `self` and cannot be loaded under node, so anything that wants a unit test is
// extracted into a module of its own. DatabaseWrapper delegates to them.

/** One conflicted doc found by the conflict-canary scan (#3987 B3). */
export interface ConflictScanDocSummary {
  id: string;
  conflictCount: number;
}

/** Aggregated result of one conflict-canary scan (#3987 B3). */
export interface ConflictScanSummary {
  conflictedDocCount: number;
  totalConflictRevs: number;
  docTypes: string[];
  docSummaries: ConflictScanDocSummary[];
}

/** The allDocs row shape the conflict-canary scan consumes. */
export interface ConflictScanRow {
  doc?: { _id: string; _conflicts?: string[] } | null;
}

/**
 * Derive a coarse doc type from a document id, for telemetry grouping
 * (#3987 B3, #3992 R3). Shared by all four sync telemetry events —
 * `sync_checkpoint_drift`, `sync_pull_catchup`, `sync_conflict_scan`, and
 * `sync_conflict_merged` (three call sites).
 *
 * This used to be a "take the text after the last dash" split, which was wrong
 * in two ways that both inflate cardinality (#4249):
 *
 * - A log id is `log/<time_utc>-<Type>`, and the timestamp contains dashes of
 *   its own. On an id with no type suffix the split returned a timestamp
 *   fragment like `17T14:00:00.000Z` and reported it as a doc type.
 * - A non-log id such as `plan/<uuid>` or `food/<uuid>` returned a piece of the
 *   uuid — a different "type" for every document in the database.
 *
 * So: match the log shape first by skipping the timestamp, and otherwise group
 * by the id's NAMESPACE (the text before the first slash). Both give a small,
 * fixed set of values.
 */
export function docTypeFromId(id: string): string {
  const text = String(id);
  // The timestamp may end in `Z`, in a zone offset (`+02:00`), or — in old test
  // fixtures — in nothing at all, so the zone group is optional. But the TYPE must
  // start with a letter. Without that anchor a type-less offset id backtracks past
  // the optional zone and reports part of the offset as a document type — e.g. a
  // colon-less `log/<iso>-0500` would come back as type "0500". Every real type is
  // an identifier (Workout, Meal, WeighIn, WeeklyCheckup, FitnessPlan …), so a
  // leading letter separates the two cases cleanly.
  const logTypeMatch = /^log\/\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:\d{2})?-([A-Za-z]\w*)$/.exec(text);
  if (logTypeMatch) return logTypeMatch[1];
  const slashIndex = text.indexOf('/');
  if (slashIndex > 0) return text.slice(0, slashIndex);
  return text.length > 0 ? text : 'unknown';
}

/**
 * Summarize the local allDocs rows of a conflict-canary scan (#3987 B3, audit
 * risk R2). Pure so the counting logic is unit-testable without a PouchDB
 * instance: skips rows without a doc, `_design/` docs, duplicate ids (the
 * scan's ranges 2 and 3 share the `log/<U+FFF0>` boundary key, so a doc could
 * theoretically appear in both), and docs with no `_conflicts` leaves; counts
 * the rest. Doc-type derivation is `docTypeFromId` above.
 */
export function summarizeConflictScan(rows: ConflictScanRow[]): ConflictScanSummary {
  let totalConflictRevs = 0;
  const docTypes = new Set<string>();
  const docSummaries: ConflictScanDocSummary[] = [];
  const seenIds = new Set<string>();
  for (const row of rows) {
    const doc = row.doc;
    if (!doc) continue;
    if (String(doc._id).startsWith('_design/')) continue; // design docs are server plumbing, not user data
    if (seenIds.has(doc._id)) continue; // a range-boundary doc must not count twice
    seenIds.add(doc._id);
    const conflictCount = doc._conflicts?.length ?? 0;
    if (conflictCount === 0) continue;
    totalConflictRevs += conflictCount;
    docTypes.add(docTypeFromId(doc._id));
    docSummaries.push({ id: doc._id, conflictCount });
  }
  return {
    conflictedDocCount: docSummaries.length,
    totalConflictRevs,
    docTypes: [...docTypes],
    docSummaries,
  };
}

/** How many document ids the "pulled from another device" summary line names
 *  before it switches to a "+N more" count (#4243). */
export const PULLED_FROM_OTHER_DEVICE_LOG_SAMPLE_SIZE = 5;

/**
 * Build the ONE-LINE, STRING-ONLY summary of the documents a replication pull
 * batch brought in from another device (#4243).
 *
 * Why string-only matters: LoggingService replaces every console method with its
 * own router. Passing anything other than a lone string sends the router down a
 * heavy path — outside prod it deep-serializes the argument
 * (JSON.parse(JSON.stringify(...))), and on every build it spreads the argument
 * into a retained Sentry breadcrumb. The old code logged the whole raw
 * replication document per pulled doc; that document carries `_revisions` (50+
 * hashes on a conflict-heavy doc), and with ~15k pulled docs on a cold start it
 * saturated the JS thread. Summarizing to one string per batch keeps the router
 * on its cheap path while preserving the "which docs came from another device"
 * signal.
 *
 * Takes the already-capped sample of ids plus the total count (rather than the
 * whole id array) so the caller holds only `sampleSize` ids in memory during a
 * large batch, never one string per pulled doc.
 *
 * Returns null when there is nothing to report, so the caller logs nothing.
 */
export function formatPulledFromOtherDeviceLog(
  sampledIds: string[],
  totalCount: number,
  myDeviceId: string,
): string | null {
  // if no doc in this batch came from another device, there is nothing to log
  if (totalCount === 0) {
    return null;
  }
  const hidden = totalCount - sampledIds.length;
  const more = hidden > 0 ? `, +${hidden} more` : '';
  return `Pulled ${totalCount} change(s) from another device (my device: ${myDeviceId}): ${sampledIds.join(', ')}${more}`;
}
